/** Heat Stroke (Steam Colossus, Juggernaut): the engine vents, and everything in
 *  front of it cooks.
 *
 * The expansion's description, and the three facts that make it a distinct action rather
 * than a bigger attack:
 *
 *   - it is spent INSTEAD of moving or attacking,
 *   - it damages friend and foe alike inside its area,
 *   - nothing retaliates against it.
 *
 * The area is "3 hexes adjacent and 4-5 hexes behind that" - seven or eight
 * hexes fanning out ahead of the machine. Written here as **every hex within
 * two steps that lies ahead of the stack**, where "ahead" is the sign of
 * `facing`. On an eleven-row field that yields seven hexes against a wall and
 * eight in open ground, which is the published spread; and expressing it as a
 * half-disc rather than a hand-listed template means it behaves correctly at
 * the board edges without a special case for each of them.
 *
 * Damage is "practically the same per target stack as a usual attack", so each
 * victim takes an ordinary computed blow rather than a flat figure - a
 * Juggernaut with Offence hits harder here too, which is what "same as a usual
 * attack" means.
 */

import { BF_HEIGHT, BF_WIDTH, hexCol, hexDistance, hexIndex, hexRow } from "./state.js";

/** How far the vent reaches. */
export const HEAT_STROKE_RANGE = 2;

/**
 * Every hex the vent covers, in ascending order.
 *
 * `facing` is +1 for a stack looking right and -1 for one looking left, which
 * is the same convention `reorient` and the two-hex stacks use. The stack's own
 * hex is excluded: it is not standing in its own exhaust.
 */
/**
 * Axial `q`, the same conversion `breathHex` uses.
 *
 * "Ahead" has to be measured in axial coordinates, not in offset columns. The
 * first version of this function tested `col > col0`, and because odd and even
 * rows are staggered by half a hex that is not a straight half-plane: the cone
 * came out at 8 hexes on some rows and 6 on others, and mirrored the wrong way
 * for a left-facing machine. Measured, row by row, it alternated 6/8 with row
 * parity - the same Steam Colossus stronger or weaker for where it happened to
 * stand. In axial space the test is exact and the shape is the same everywhere.
 */
const axialQ = (h: number): number =>
  hexCol(h) - ((hexRow(h) - (hexRow(h) & 1)) >> 1);

export function heatStrokeHexes(from: number, facing: number, out: number[]): number {
  const dir = facing < 0 ? -1 : 1;
  const q0 = axialQ(from);
  const row0 = hexRow(from);
  let n = 0;
  for (let row = row0 - HEAT_STROKE_RANGE; row <= row0 + HEAT_STROKE_RANGE; row++) {
    if (row < 0 || row >= BF_HEIGHT) continue;
    for (let col = 0; col < BF_WIDTH; col++) {
      // `hexIndex`, not `row * BF_WIDTH + col`. The same arithmetic written
      // twice is the defect this project spent a day finding: the original
      // moves and the copy does not, and nothing says which is authoritative.
      const h = hexIndex(col, row);
      if (h === from) continue;
      // Ahead of the machine, never behind it and never level with it.
      if (dir > 0 ? axialQ(h) <= q0 : axialQ(h) >= q0) continue;
      if (hexDistance(from, h) > HEAT_STROKE_RANGE) continue;
      out[n++] = h;
    }
  }
  return n;
}
